import { Primitive } from '@radix-ui/react-primitive'
import { clsx } from 'clsx'
import * as React from 'react'
import { useId, useState } from 'react'
import LightboxComponent from 'yet-another-react-lightbox'
import type {
  ControllerRef,
  LightboxExternalProps,
  Slide,
  ThumbnailsRef,
  ZoomRef,
  PDFSlide,
} from 'yet-another-react-lightbox'
import Thumbnails from 'yet-another-react-lightbox/plugins/thumbnails'
import 'yet-another-react-lightbox/plugins/thumbnails.css'
import Video from 'yet-another-react-lightbox/plugins/video'
import Zoom from 'yet-another-react-lightbox/plugins/zoom'
import 'yet-another-react-lightbox/styles.css'

import usePortalContainer from '../../helpers/usePortalContainer'
import { useControllableState } from '../../hooks/useControllableState'
import { Button } from '../Button'
import { CloseButton, NextButton, PrevButton, ZoomControls } from './Controls'
import { Counter } from './Counter'

import styles from './Lightbox.module.css'

declare module 'yet-another-react-lightbox' {
  export interface PDFSlide extends GenericSlide {
    type: 'pdf'
    src: string
  }

  interface SlideTypes {
    pdf: PDFSlide
  }
}

function isPDFSlide(slide: Slide): slide is PDFSlide {
  return slide.type === 'pdf'
}

/**
 * Validates and sanitizes a URL to prevent XSS attacks
 * @param url - The URL string to validate
 * @returns The validated URL string or null if invalid/unsafe
 */
function validateUrl(url: string): string | null {
  if (typeof window === 'undefined') return null
  try {
    const parsed = new URL(url, window.location.origin)

    // Allow only safe protocols
    const safeProtocols = ['https:', 'http:', 'blob:']
    if (!safeProtocols.includes(parsed.protocol)) {
      return null
    }

    // Additional check: reject javascript: and data: schemes
    if (
      url.trim().toLowerCase().startsWith('javascript:') ||
      url.trim().toLowerCase().startsWith('data:')
    ) {
      return null
    }

    return parsed.href
  } catch {
    return null
  }
}

/* -------------------------------------------------------------------------------------------------
 * LightboxContext
 * ----------------------------------------------------------------------------------------------- */

interface LightboxContextType {
  open: boolean
  contentId: string
  onOpenToggle: (options?: { index?: number }) => void
}
const LightboxContext = React.createContext<LightboxContextType | null>(null)

function useLightboxContext(): LightboxContextType {
  const context = React.useContext(LightboxContext)

  if (!context) {
    throw new Error(
      'Lightbox compound components cannot be rendered outside the Lightbox component',
    )
  }

  return context
}

/* -------------------------------------------------------------------------------------------------
 * Lightbox
 * ----------------------------------------------------------------------------------------------- */

interface LightboxProps
  extends
    React.ComponentPropsWithoutRef<'div'>,
    Partial<LightboxExternalProps> {
  slides: Slide[]
  defaultOpen?: boolean
  onOpenChange?: (open: boolean) => void
}

const Lightbox = React.forwardRef<ControllerRef, LightboxProps>(
  (
    {
      open: openProp,
      defaultOpen,
      onOpenChange,
      children,
      index: providedIndex = 0,
      render,
      ...restProps
    }: LightboxProps,
    forwardedRef,
  ) => {
    const [open = false, setOpen] = useControllableState({
      prop: openProp,
      defaultProp: defaultOpen,
      onChange: onOpenChange,
    })
    const portal = usePortalContainer()
    const [index, setIndex] = useState(providedIndex || 0)

    const thumbnailsRef = React.useRef<ThumbnailsRef>(null)

    // Need the following for tracking whether zooming in/out is possible
    const zoomRef = React.useRef<ZoomRef>(null)
    const [zoomLevel, setZoomLevel] = React.useState(1)
    // The default helps for the first render until the ref is assigned
    const maxZoom: number = zoomRef.current?.maxZoom || 5
    const onOpenToggle = React.useCallback(
      (options?: { index?: number }) => {
        if (options?.index !== undefined) setIndex(options.index)
        setOpen((prevOpen) => !prevOpen)
      },
      [setOpen],
    )

    return (
      <LightboxContext.Provider
        value={{ open, contentId: useId(), onOpenToggle }}
      >
        <LightboxComponent
          portal={{ root: portal }}
          controller={{ ref: forwardedRef, closeOnBackdropClick: true }}
          open={open}
          index={index}
          close={() => setOpen(false)}
          className={styles.lightbox}
          carousel={{
            // This allows thumbnails to cycle from left to right, instead of an infinite loop
            finite: true,
            // The preloads the thumbnails as well
            preload: 10,
          }}
          zoom={{ ref: zoomRef, maxZoomPixelRatio: 3 }}
          plugins={[
            Counter,
            Thumbnails,
            Video,
            ({ augment }): void => {
              augment(({ render: pluginRender, ...props }) => ({
                ...props,
                render: {
                  ...pluginRender,
                  slide: (slideProps): React.ReactNode =>
                    render?.slide?.(slideProps) ??
                    pluginRender.slide?.(slideProps),
                },
              }))
            },
            Zoom,
          ]}
          animation={{
            // Disable swipe animation when changing slides
            swipe: 0,
          }}
          on={{
            exited: () => setOpen(false),
            zoom: ({ zoom }) => setZoomLevel(zoom),
            entering: () => {
              if (thumbnailsRef.current && restProps.slides.length <= 1)
                thumbnailsRef.current.hide()
            },
          }}
          thumbnails={{
            ref: thumbnailsRef,
            width: 80,
            height: 80,
            border: 2,
            imageFit: 'cover',
            padding: 0,
            vignette: false,
          }}
          render={{
            buttonClose: () => <CloseButton key="close" />,
            buttonNext: restProps.slides.length <= 1 ? () => null : NextButton,
            buttonPrev: restProps.slides.length <= 1 ? () => null : PrevButton,
            // Replace default zoom button with custom controls
            buttonZoom: () => null,
            controls: () => (
              <ZoomControls
                zoomLevel={zoomLevel}
                maxZoom={maxZoom}
                zoomIn={zoomRef.current?.zoomIn}
                zoomOut={zoomRef.current?.zoomOut}
              />
            ),
            ...render,
            slide: ({ slide }) => {
              if (!isPDFSlide(slide)) return undefined

              const validatedUrl = validateUrl(slide.src)

              if (!validatedUrl) {
                return (
                  <div className={styles.pdfPreviewContainer}>
                    <p>Unable to display PDF: Invalid or unsafe URL</p>
                  </div>
                )
              }

              return (
                <object
                  data={validatedUrl}
                  type="application/pdf"
                  className={styles.pdfPreviewContainer}
                >
                  <iframe
                    src={validatedUrl}
                    width="100%"
                    height="100%"
                    className={styles.pdfIframeFallback}
                    title="Fallback PDF Viewer"
                    sandbox="allow-downloads"
                  >
                    <p>
                      Your browser does not support PDFs.{' '}
                      <Button
                        as="a"
                        href={validatedUrl}
                        target="_blank"
                        rel="noreferrer noopener"
                      >
                        Download
                      </Button>
                    </p>
                  </iframe>
                </object>
              )
            },
          }}
          {...restProps}
        />
        {children}
      </LightboxContext.Provider>
    )
  },
)

Lightbox.displayName = 'Lightbox'

/* -------------------------------------------------------------------------------------------------
 * LightboxTrigger
 * ----------------------------------------------------------------------------------------------- */

function getState(open: boolean) {
  return open ? 'open' : 'closed'
}

type LightboxTriggerElement = React.ElementRef<typeof Primitive.button>
type PrimitiveButtonProps = React.ComponentPropsWithoutRef<
  typeof Primitive.button
>
type LightboxTriggerProps = PrimitiveButtonProps & {
  index?: number
}

const LightboxTrigger = React.forwardRef<
  LightboxTriggerElement,
  LightboxTriggerProps
>(
  (
    { className, index, ...triggerProps }: LightboxTriggerProps,
    forwardedRef,
  ) => {
    const context = useLightboxContext()

    const onOpen = React.useCallback(() => {
      context.onOpenToggle({ index })
    }, [context, index])

    return (
      <Primitive.button
        ref={forwardedRef}
        type="button"
        className={clsx(styles.trigger, className)}
        aria-haspopup="dialog"
        aria-expanded={context.open}
        aria-controls={context.contentId}
        data-state={getState(context.open)}
        {...triggerProps}
        onClick={onOpen}
      />
    )
  },
)

LightboxTrigger.displayName = 'LightboxTrigger'

const LightboxObject = Object.assign(Lightbox, {
  Trigger: LightboxTrigger,
})

export { LightboxObject as Lightbox }
export type {
  LightboxProps,
  LightboxTriggerProps,
  ControllerRef,
  ThumbnailsRef,
  ZoomRef,
}
